import React, { useEffect, useMemo, useRef, useState } from 'react';
import './Brand24ProjectConfigDialog.scss';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { useTranslation } from 'react-i18next';
import {
  Brand24Service,
  Brand24Project,
  Brand24BrandInput,
  Brand24ProjectRole,
} from '../../services/integrations/Brand24Service';
import { useGetProject } from '../../api/hooks/project/useGetProject';
import { useGetCommonKeywordsWithTags } from '../../api/hooks/monitor/keyword/useGetCommonKeywordsWithTags';
import { useGetTags } from '../../api/hooks/tags/useGetTags';
import { useGetMonitorsForProject } from '../../api/hooks/monitor/useGetMonitorsForProject';
import { useGetProjectAllBrandsStats } from '../../api/hooks/project/stats/useGetProjectAllBrandsStats';
import { getTagLabel } from '../../helpers/tags/TagsHelper';
import { getPeriod, prepareAndGetChatType } from '../../helpers/StatsHelpers';
import { getDefaultRange } from '../../utils/utils';
import { TAG_NAME } from '../../utils/urls';

interface Brand24ProjectConfigDialogProps {
  visible: boolean;
  onHide: () => void;
  chatbeatProjectId: number;
  /** OWN_API_KEY unlocks the main-brand dropdown and adds a per-row Brand24 project selector. */
  isOwnMode: boolean;
  /** Fires with the configured brands; the parent runs the activation animation + the API call. */
  onSubmit?: (brands: Brand24BrandInput[]) => void;
}

interface BrandOption {
  value: string; // chatbeatBrand: normalizedKeyword or chatbeat_tag_name_<id>
  label: string;
}

interface Row {
  uid: number;
  role: Brand24ProjectRole;
  brand: string; // '' = not chosen yet
  brand24ProjectId: number; // OWN mode: 0 = create new, >0 = an existing project id
}

// Up to this many competitor rows (the top brands by share of voice); the user can remove any of them.
const MAX_COMPETITORS = 4;

const Brand24ProjectConfigDialog: React.FC<Brand24ProjectConfigDialogProps> = ({
  visible,
  onHide,
  chatbeatProjectId,
  isOwnMode,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const projectId = chatbeatProjectId || null;

  const { project } = useGetProject(projectId);
  const { commonKeywords } = useGetCommonKeywordsWithTags(projectId);
  const { tags } = useGetTags(projectId);
  const { monitorPrompts } = useGetMonitorsForProject(projectId);

  // Mirror the filters a project opens with by default (no URL period/chat params): DAILY, ALL models,
  // the default date range — so the SoV ranking here matches the one on the project preview page 1-1.
  const [range] = useState(() => getDefaultRange());
  const { allBrandsStatsResponse, isLoading: isSovLoading } = useGetProjectAllBrandsStats(
    projectId,
    monitorPrompts ?? null,
    prepareAndGetChatType(null, 'ALL'),
    getPeriod(undefined),
    range[0] ?? null,
    range[1] ?? null,
    undefined,
    true
  );

  // Existing Brand24 projects (OWN mode only) for the per-row "pick an existing project" selector.
  const [existingProjects, setExistingProjects] = useState<Brand24Project[]>([]);
  const [mainRow, setMainRow] = useState<Row>({ uid: 0, role: 'MAIN', brand: '', brand24ProjectId: 0 });
  const [competitorRows, setCompetitorRows] = useState<Row[]>([]);
  const initialized = useRef(false);
  const nextUid = useRef(1);

  const makeRow = (brand: string): Row => ({
    uid: nextUid.current++,
    role: 'COMPETITOR',
    brand,
    brand24ProjectId: 0,
  });

  // value -> option and display-label -> value: brands are keyed by normalizedKeyword / tag key; the SoV
  // endpoint returns display labels, so we need both directions.
  const { optionList, optionByValue, valueByLabel } = useMemo(() => {
    const byValue = new Map<string, BrandOption>();
    const byLabel = new Map<string, string>();
    (commonKeywords ?? []).forEach((k) => {
      const value = k.tagId != null ? `${TAG_NAME}${k.tagId}` : k.normalizedKeyword;
      const label = getTagLabel(tags, k.tagId ?? null, k.keyword);
      if (!byValue.has(value)) {
        byValue.set(value, { value, label });
        byLabel.set(label, value);
      }
    });
    return {
      optionList: Array.from(byValue.values()),
      optionByValue: byValue,
      valueByLabel: byLabel,
    };
  }, [commonKeywords, tags]);

  const mainOption = useMemo<BrandOption | null>(() => {
    if (!project?.firmName) return null;
    const existing = optionByValue.get(project.firmName);
    if (existing) return existing;
    const label = project.firmName.startsWith(TAG_NAME)
      ? getTagLabel(tags, Number(project.firmName.slice(TAG_NAME.length)), project.firmName)
      : project.firmName;
    return { value: project.firmName, label };
  }, [project, optionByValue, tags]);

  useEffect(() => {
    if (!visible) {
      initialized.current = false;
      return;
    }
    if (isOwnMode && existingProjects.length === 0) {
      Brand24Service.getProjects().then(setExistingProjects).catch(() => setExistingProjects([]));
    }
  }, [visible, isOwnMode, existingProjects.length]);

  // Prefill once per open, after the brand list, the main brand and the SoV ranking have settled.
  useEffect(() => {
    if (!visible || initialized.current || !mainOption || isSovLoading) return;

    const rankedBySov = [...allBrandsStatsResponse]
      .sort((a, b) => b.shareOfVoice - a.shareOfVoice)
      .map((r) => valueByLabel.get(r.keyword))
      .filter((v): v is string => !!v);

    const pool = rankedBySov.length > 0 ? rankedBySov : optionList.map((o) => o.value);
    const top: string[] = [];
    const seen = new Set<string>([mainOption.value]);
    for (const value of pool) {
      if (seen.has(value)) continue;
      seen.add(value);
      top.push(value);
      if (top.length === MAX_COMPETITORS) break;
    }

    setMainRow({ uid: 0, role: 'MAIN', brand: mainOption.value, brand24ProjectId: 0 });
    setCompetitorRows(top.map((value) => makeRow(value)));
    initialized.current = true;
  }, [visible, mainOption, isSovLoading, allBrandsStatsResponse, optionList, valueByLabel]);

  const brandOptions = useMemo(() => {
    const list = [...optionList];
    if (mainOption && !optionByValue.has(mainOption.value)) list.unshift(mainOption);
    return list;
  }, [optionList, mainOption, optionByValue]);

  const b24Options = useMemo(
    () => [
      { value: 0, label: t('brand24Config.createNew') },
      ...existingProjects.map((p) => ({ value: p.id, label: p.name })),
    ],
    [existingProjects, t]
  );

  const updateCompetitor = (uid: number, patch: Partial<Row>) =>
    setCompetitorRows((rows) => rows.map((r) => (r.uid === uid ? { ...r, ...patch } : r)));
  const removeCompetitor = (uid: number) =>
    setCompetitorRows((rows) => rows.filter((r) => r.uid !== uid));
  const addCompetitor = () =>
    setCompetitorRows((rows) => (rows.length < MAX_COMPETITORS ? [...rows, makeRow('')] : rows));

  const buildBrandInput = (row: Row): Brand24BrandInput => {
    const input: Brand24BrandInput = {
      role: row.role,
      chatbeatBrand: String(row.brand),
      chatbeatBrandLabel: optionByValue.get(row.brand)?.label ?? String(row.brand),
    };
    if (isOwnMode && row.brand24ProjectId > 0) {
      input.brand24ProjectId = row.brand24ProjectId;
      input.brand24ProjectName = existingProjects.find((p) => p.id === row.brand24ProjectId)?.name;
    }
    return input;
  };

  const handleContinue = () => {
    const rows = [mainRow, ...competitorRows].filter((r) => r.brand);
    if (rows.length === 0) return;
    onSubmit?.(rows.map(buildBrandInput));
    onHide();
  };

  const renderRow = (row: Row, isMain: boolean, position: number) => (
    <div className="brand24-config__row" key={isMain ? 'main' : row.uid}>
      <span className="brand24-config__role">
        {isMain ? t('brand24Config.mainBrand') : t('brand24Config.competitor', { n: position })}
      </span>
      <div className="brand24-config__controls">
        <Dropdown
          className="brand24-config__dropdown dropdown"
          value={row.brand}
          options={brandOptions}
          optionLabel="label"
          optionValue="value"
          // DEFAULT mode locks the main brand to the project's brand; OWN mode lets the user remap it.
          disabled={isMain && !isOwnMode}
          placeholder={t('brand24Config.pickBrand')}
          onChange={(e) =>
            isMain
              ? setMainRow({ ...mainRow, brand: e.value })
              : updateCompetitor(row.uid, { brand: e.value })
          }
        />
        {isOwnMode && (
          <>
            <span className="brand24-config__arrow">→</span>
            <Dropdown
              className="brand24-config__dropdown dropdown"
              value={row.brand24ProjectId}
              options={b24Options}
              optionLabel="label"
              optionValue="value"
              disabled={!isMain && !row.brand}
              onChange={(e) =>
                isMain
                  ? setMainRow({ ...mainRow, brand24ProjectId: e.value })
                  : updateCompetitor(row.uid, { brand24ProjectId: e.value })
              }
            />
          </>
        )}
        {isMain ? (
          <span className="brand24-config__remove-placeholder" aria-hidden="true" />
        ) : (
          <button
            type="button"
            className="action-btn action-btn--danger brand24-config__remove"
            aria-label={t('brand24Config.removeCompetitor')}
            title={t('brand24Config.removeCompetitor')}
            onClick={() => removeCompetitor(row.uid)}
          >
            <span className="ico--trash ico--xs" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <Dialog
      header={t('brand24Config.title')}
      visible={visible}
      onHide={onHide}
      className="dialog-box brand24-config"
      style={{ width: isOwnMode ? '620px' : '480px' }}
      footer={
        <button className="btn btn--sm" type="button" disabled={!mainRow.brand} onClick={handleContinue}>
          {t('brand24Config.continue')}
        </button>
      }
    >
      <div className="brand24-config__intro">{t('brand24Config.intro')}</div>

      <div className="brand24-config__rows">
        {renderRow(mainRow, true, 0)}
        {competitorRows.map((row, i) => renderRow(row, false, i + 1))}
      </div>

      {competitorRows.length < MAX_COMPETITORS && (
        <button type="button" className="brand24-config__add" onClick={addCompetitor}>
          <span className="brand24-config__add-icon">+</span> {t('brand24Config.addCompetitor')}
        </button>
      )}
    </Dialog>
  );
};

export default Brand24ProjectConfigDialog;
